import { useEffect, useMemo, useRef } from 'react';
import { AutoSizer, CellMeasurer, CellMeasurerCache, List, WindowScroller } from 'react-virtualized';

import useScreenSize from '@/hooks/useScreenSize';
import InfiniteScroll from '@/shared-components/lists/components/InfiniteScroll';

const ORDER_CARD_GAP = 20;

export default function MeasuredOrderVirtualList({
  orders,
  totalOrders,
  loadMoreData,
  defaultRowHeight,
  layoutVersion,
  renderOrder,
}) {
  const screenSize = useScreenSize();
  const listRef = useRef();
  const ordersRef = useRef(orders);
  ordersRef.current = orders;
  const cellCache = useMemo(() => new CellMeasurerCache({
    defaultHeight: defaultRowHeight,
    fixedWidth: true,
    keyMapper: (rowIndex) => ordersRef.current[rowIndex]?._id ?? rowIndex,
  }), [defaultRowHeight]);

  useEffect(() => {
    cellCache.clearAll();
    listRef.current?.recomputeRowHeights();
  }, [cellCache, layoutVersion, screenSize.width]);

  return (
    <InfiniteScroll
      onLoadMore={loadMoreData}
      shouldLoadMore={orders.length < totalOrders}
      offsetElements={3}
      listLength={orders.length}
    >
      {({ infiniteScrollAssigner }) => (
        <WindowScroller>
          {({ height, isScrolling, onChildScroll, scrollTop }) => (
            <AutoSizer disableHeight>
              {({ width }) => (
                <List
                  autoHeight
                  width={width === 0 ? 0.1 : width}
                  height={height}
                  isScrolling={isScrolling}
                  onScroll={onChildScroll}
                  scrollTop={scrollTop}
                  ref={listRef}
                  overscanRowCount={3}
                  rowCount={orders.length}
                  deferredMeasurementCache={cellCache}
                  rowHeight={cellCache.rowHeight}
                  rowRenderer={({ index, key, parent, style }) => (
                    <CellMeasurer
                      key={key}
                      cache={cellCache}
                      parent={parent}
                      rowIndex={index}
                      columnIndex={0}
                    >
                      {({ registerChild }) => {
                        const assignScrollRef = infiniteScrollAssigner(index);
                        const combinedRef = (element) => {
                          registerChild(element);
                          assignScrollRef?.(element);
                        };

                        return (
                          <div
                            ref={combinedRef}
                            style={{ ...style, paddingBottom: ORDER_CARD_GAP, boxSizing: 'border-box' }}
                          >
                            {renderOrder(orders[index], index)}
                          </div>
                        );
                      }}
                    </CellMeasurer>
                  )}
                />
              )}
            </AutoSizer>
          )}
        </WindowScroller>
      )}
    </InfiniteScroll>
  );
}
